import { apiClient } from '@rti/shared';
import type { DieselPriceRow } from '@rti/shared';

export async function getCachedDieselPrices(): Promise<DieselPriceRow[]> {
  const data = await apiClient.readDataFromCache<DieselPriceRow[]>('DIESEL_PRICES');
  return data || [];
}

function filterByDateRange(data: any[], dateFrom?: string, dateTo?: string): any[] {
  if (!dateFrom && !dateTo) return data;
  return data.filter((row) => {
    const d = row.date || row.Date || row.period;
    if (!d) return true;
    const ds = String(d).slice(0, 10);
    if (dateFrom && ds < dateFrom) return false;
    if (dateTo && ds > dateTo) return false;
    return true;
  });
}

export async function getPEInventoryMetrics(
  resinType: 'pe' | 'hdpe' | 'ldpe' | 'lldpe' = 'pe',
  dateFrom?: string,
  dateTo?: string
): Promise<any[]> {
  const all = await apiClient.fetchCachedPEInventoryMetrics();
  if (!all) {
    console.warn(`⚠️ No data returned from API`);
    return [];
  }
  const filtered = all.filter((r: any) => r.resin_type === resinType);
  const result = filterByDateRange(filtered, dateFrom, dateTo);
  return result;
}

export async function getPEACCCommentaryData(
  resinType: 'pe' | 'hdpe' | 'ldpe' | 'lldpe' = 'pe'
): Promise<any[]> {
  const cacheKeyMap = {
    pe: 'PE_COMMENTARY',
    hdpe: 'HDPE_COMMENTARY',
    ldpe: 'LDPE_COMMENTARY',
    lldpe: 'LLDPE_COMMENTARY',
  } as const;
  const cacheKey = cacheKeyMap[resinType];

  try {
    const data = await apiClient.readDataFromCache<any[]>(cacheKey);
    return data || [];
  } catch (err) {
    console.error(`getPEACCCommentaryData [${resinType}] - Failed to fetch`, err);
    return [];
  }
}

export async function getPEProductionCapacity(
  dateFrom?: string,
  dateTo?: string
): Promise<any[]> {
  const all = await apiClient.fetchCachedPEProductionCapacity();
  if (!all) return [];
  return filterByDateRange(all, dateFrom, dateTo);
}

export async function getPEMarkets(
  resinType: 'hdpe' | 'ldpe' | 'lldpe' = 'hdpe',
  dateFrom?: string,
  dateTo?: string
): Promise<any[]> {
  const all = await apiClient.fetchCachedPEMarkets();
  if (!all) return [];
  const filtered = all.filter((r: any) => r.resin_type === resinType);
  return filterByDateRange(filtered, dateFrom, dateTo);
}

export async function getPERates(dateFrom?: string, dateTo?: string): Promise<any[]> {
  const all = await apiClient.fetchCachedPERates();
  if (!all) return [];
  return filterByDateRange(all, dateFrom, dateTo);
}

export async function getPEUSITCTrade(
  tradeDirection: 'import' | 'export' = 'import',
  dateFrom?: string,
  dateTo?: string
): Promise<any[]> {
  const all = await apiClient.fetchCachedPEUSITCTrade();
  if (!all) return [];
  const filtered = all.filter((r: any) => r.trade_direction === tradeDirection);
  return filterByDateRange(filtered, dateFrom, dateTo);
}

export async function getFairPriceIndexCached(dateFrom?: string, dateTo?: string): Promise<any[]> {
  const all = await apiClient.fetchCachedFairPriceData();
  if (!all) return [];
  const arr = Array.isArray(all) ? all : [];
  return filterByDateRange(arr, dateFrom, dateTo);
}

export async function getCachedACCData(resin: 'pp' | 'ps' | 'pvc'): Promise<any[]> {
  const keyMap = { pp: 'ACC_PP', ps: 'ACC_PS', pvc: 'ACC_PVC' } as const;
  const data = await apiClient.readDataFromCache<any[]>(keyMap[resin]);
  return data || [];
}

export async function getCachedACCFinalData(resin: 'pe' | 'pp' | 'ps' | 'pvc'): Promise<any[]> {
  const keyMap = {
    pe: 'ACC_PE_FINAL',
    pp: 'ACC_PP_FINAL',
    ps: 'ACC_PS_FINAL',
    pvc: 'ACC_PVC_FINAL',
  } as const;
  const data = await apiClient.readDataFromCache<any[]>(keyMap[resin]);
  return data || [];
}

export async function getCachedRefineryRates(): Promise<any[]> {
  const data = await apiClient.fetchCachedRefineryRatesData();
  return data ? (Array.isArray(data) ? data : (data as any).data || []) : [];
}

export async function getCachedPPRates(dateFrom?: string, dateTo?: string): Promise<any[]> {
  const all = await apiClient.fetchCachedPPRates();
  if (!all) return [];
  return filterByDateRange(Array.isArray(all) ? all : [], dateFrom, dateTo);
}

export async function getPSInventoryMetrics(dateFrom?: string, dateTo?: string): Promise<any[]> {
  const all = await apiClient.fetchCachedPSInventoryMetrics();
  if (!all) return [];
  return filterByDateRange(Array.isArray(all) ? all : [], dateFrom, dateTo);
}

export async function getPPInventoryMetrics(dateFrom?: string, dateTo?: string): Promise<any[]> {
  const all = await apiClient.fetchCachedPPInventoryMetrics();
  if (!all) return [];
  return filterByDateRange(Array.isArray(all) ? all : [], dateFrom, dateTo);
}

export async function getPVCInventoryMetrics(dateFrom?: string, dateTo?: string): Promise<any[]> {
  const all = await apiClient.fetchCachedPVCInventoryMetrics();
  if (!all) return [];
  return filterByDateRange(Array.isArray(all) ? all : [], dateFrom, dateTo);
}

export async function getCachedPSRates(dateFrom?: string, dateTo?: string): Promise<any[]> {
  const all = await apiClient.fetchCachedPSRates();
  if (!all) return [];
  return filterByDateRange(Array.isArray(all) ? all : [], dateFrom, dateTo);
}

export async function getCachedPVCRates(dateFrom?: string, dateTo?: string): Promise<any[]> {
  const all = await apiClient.fetchCachedPVCRates();
  if (!all) return [];
  return filterByDateRange(Array.isArray(all) ? all : [], dateFrom, dateTo);
}

export async function getCachedPETRates(dateFrom?: string, dateTo?: string): Promise<any[]> {
  const all = await apiClient.fetchCachedPETRates();
  if (!all) return [];
  return filterByDateRange(Array.isArray(all) ? all : [], dateFrom, dateTo);
}

export async function getCachedPPUSITCTrade(dateFrom?: string, dateTo?: string): Promise<any[]> {
  const all = await apiClient.fetchCachedPPUSITCTrade();
  if (!all) return [];
  return filterByDateRange(Array.isArray(all) ? all : [], dateFrom, dateTo);
}

export async function getCachedPSUSITCTrade(dateFrom?: string, dateTo?: string): Promise<any[]> {
  const all = await apiClient.fetchCachedPSUSITCTrade();
  if (!all) return [];
  return filterByDateRange(Array.isArray(all) ? all : [], dateFrom, dateTo);
}

export async function getCachedPVCUSITCTrade(dateFrom?: string, dateTo?: string): Promise<any[]> {
  const all = await apiClient.fetchCachedPVCUSITCTrade();
  if (!all) return [];
  return filterByDateRange(Array.isArray(all) ? all : [], dateFrom, dateTo);
}

export async function getCachedPETUSITCTrade(dateFrom?: string, dateTo?: string): Promise<any[]> {
  const all = await apiClient.fetchCachedPETUSITCTrade();
  if (!all) return [];
  return filterByDateRange(Array.isArray(all) ? all : [], dateFrom, dateTo);
}
